export const SITE = {
  name: "Empire Serv, Inc.",
  short: "Empire Serv",
  tagline: "Mission-Critical Facility Maintenance",
  brand_statement:
    "Empire Serv keeps mission-critical facilities operating at their highest standard through reliable, compliant, and safety-driven facility maintenance. From hospitals and manufacturing plants to government facilities, we deliver consistent performance, protect critical environments, and build long-term partnerships through operational excellence.",
  phone: "855-85-WE CLEAN",
  phoneHref: "tel:18558593253",
  email: "support@empireserv.com",
  address: {
    line1: "15 S. Fayetteville Street, Suite 203-F",
    city: "Asheboro",
    state: "NC",
    zip: "27203",
  },
  hours: "Mon–Fri · 8:00 AM – 5:30 PM ET",
  logo: "https://customer-assets-jai6qajn.emergentagent.net/job_facilities-trust-pro/artifacts/2dbb36d9ab3d5ade_Empire%20Serv%20Logo%202026.jpg",
  founded: 2000,
};

export const HERO_METRICS = [
  { value: "25+", label: "Years of experience" },
  { value: "500+", label: "Facilities supported" },
  { value: "99.7%", label: "Client retention" },
  { value: "Zero", label: "Incident culture" },
];

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Government", to: "/government" },
  { label: "Certifications", to: "/certifications" },
  { label: "Safety", to: "/safety" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  { slug: "medical-cleaning", title: "Medical Facility Cleaning", icon: "FirstAid", blurb: "Hospital-grade infection control, HAI-reduction protocols, and audit-ready documentation for regulated healthcare environments." },
  { slug: "industrial-cleaning", title: "Industrial Cleaning", icon: "Factory", blurb: "Plant, warehouse, and cleanroom sanitation delivered by OSHA-trained crews with 5S discipline." },
  { slug: "government-maintenance", title: "Government Facility Maintenance", icon: "Bank", blurb: "Contract-ready facility services for federal, state, and municipal buildings — cleared personnel available." },
  { slug: "floor-care", title: "Floor Care", icon: "Broom", blurb: "Strip, wax, buff, refinish, and restore — VCT, terrazzo, hardwood, concrete, and luxury vinyl." },
  { slug: "window-cleaning", title: "Window Cleaning", icon: "SquaresFour", blurb: "Interior and exterior glass programs for lobbies, atriums, and multi-story facilities." },
  { slug: "pressure-washing", title: "Pressure Washing", icon: "WaveTriangle", blurb: "Building exteriors, walkways, dumpster pads, parking structures, and equipment yards." },
  { slug: "day-porter", title: "Day Porter Services", icon: "UsersThree", blurb: "On-site, uniformed daytime staff who keep lobbies, restrooms, and common areas presentation-ready." },
  { slug: "post-construction", title: "Post-Construction Cleanup", icon: "Hammer", blurb: "Rough, final, and white-glove cleanup for new builds, tenant improvements, and major renovations." },
  { slug: "preventative-maintenance", title: "Preventative Facility Maintenance", icon: "Wrench", blurb: "Scheduled inspections and light-repair programs that extend asset life and lower emergency spend." },
];

export const INDUSTRIES = [
  { slug: "healthcare", name: "Healthcare", image: "https://images.pexels.com/photos/36595248/pexels-photo-36595248.jpeg",
    points: ["Hospitals & surgical centers", "Outpatient clinics", "Behavioral health", "HAI-reduction protocols"] },
  { slug: "industrial", name: "Industrial Manufacturing", image: "https://images.pexels.com/photos/257636/pexels-photo-257636.jpeg",
    points: ["Plants & production floors", "Cleanrooms", "Warehousing", "5S & OSHA-trained crews"] },
  { slug: "government", name: "Government", image: "https://images.pexels.com/photos/17718824/pexels-photo-17718824.jpeg",
    points: ["Federal facilities", "State agencies", "Courthouses", "Contract-vehicle ready"] },
  { slug: "office", name: "Commercial Offices", image: "https://images.pexels.com/photos/36286291/pexels-photo-36286291.jpeg",
    points: ["Class A towers", "Corporate campuses", "Day porter support", "LEED-friendly programs"] },
  { slug: "education", name: "Education", image: "https://images.pexels.com/photos/8617715/pexels-photo-8617715.jpeg",
    points: ["K-12 districts", "Universities", "Green-chemistry programs", "Absenteeism reduction"] },
  { slug: "distribution", name: "Distribution Centers", image: "https://images.pexels.com/photos/1267324/pexels-photo-1267324.jpeg",
    points: ["3PL & e-commerce", "Cross-dock facilities", "Scrubber fleet management", "24/7 ops support"] },
  { slug: "municipal", name: "Municipal Facilities", image: "https://images.pexels.com/photos/276472/pexels-photo-276472.jpeg",
    points: ["City halls & libraries", "Recreation centers", "Fire & police stations", "Public-space cleanliness"] },
];

export const CERTIFICATIONS = [
  { name: "ISO 9001:2015", desc: "Quality Management Systems certified — repeatable, auditable processes." },
  { name: "CIMS-GB w/ Honors", desc: "Cleaning Industry Management Standard — Green Building, honors level." },
  { name: "OSHA 30 Trained", desc: "Field supervisors complete the OSHA 30-hour general industry program." },
  { name: "EPA Safer Choice", desc: "Chemistries vetted against EPA Safer Choice ingredient criteria." },
  { name: "GBAC STAR™", desc: "Global Biorisk Advisory Council STAR accredited disinfection protocols." },
  { name: "Green Seal GS-42", desc: "Green Seal certified commercial cleaning service standard." },
];

export const GOV_DATA = {
  uei: "Available upon RFP request",
  cage: "Available upon RFP request",
  sam: "Active in SAM.gov",
  naics: ["561720 — Janitorial Services", "561740 — Carpet & Upholstery Cleaning", "561790 — Other Services to Buildings", "238290 — Other Building Equipment Contractors", "562910 — Remediation Services"],
  vehicles: ["GSA Schedule (Multiple Award)", "State of NC Term Contracts", "Local & Municipal IDIQs", "Prime & Sub-contract ready"],
  small_business: ["Small Business", "Self-Performing Workforce", "Veteran-Friendly Employer"],
};

export const CASE_STUDIES = [
  {
    slug: "regional-medical-center",
    sector: "Healthcare",
    title: "Regional Medical Center — 380,000 sq ft, 24/7",
    challenge: "Surgical wing required HAI-reduction protocols and an audit-ready paper trail across three shifts.",
    solution: "Dedicated OR-trained crew, electrostatic disinfection, and bar-coded room logs integrated with EVS reporting.",
    result: "21% reduction in HAI incident reports YoY · 100% on-time audit readiness · 4.8 / 5 patient cleanliness score.",
    image: "https://images.pexels.com/photos/7108399/pexels-photo-7108399.jpeg",
  },
  {
    slug: "federal-courthouse",
    sector: "Government",
    title: "Federal Courthouse — Multi-floor Secure Facility",
    challenge: "Contract required cleared personnel, after-hours access, and chain-of-custody for sensitive areas.",
    solution: "Background-cleared crew, biometric check-in, and quarterly QA inspections with deficiency-free scores.",
    result: "3 consecutive option years exercised · 0 security incidents · 99.6% inspection score average.",
    image: "https://images.pexels.com/photos/17718824/pexels-photo-17718824.jpeg",
  },
  {
    slug: "advanced-mfg-plant",
    sector: "Industrial",
    title: "Advanced Manufacturing Plant — 1.2M sq ft",
    challenge: "Cleanroom, production floor, and admin offices on a single SLA with strict downtime windows.",
    solution: "Zoned crew assignments, scrubber fleet, and Kaizen-driven 5S program embedded with plant ops.",
    result: "37% drop in slip incidents · 22% chemical cost reduction · zero unplanned downtime.",
    image: "https://images.pexels.com/photos/257636/pexels-photo-257636.jpeg",
  },
  {
    slug: "k12-district",
    sector: "Education",
    title: "K-12 District — 14 Schools",
    challenge: "Standardize cleanliness, reduce absenteeism, and switch to green chemistries district-wide.",
    solution: "Single-source program with Green Seal products, electrostatic foggers in flu season, and parent-facing dashboard.",
    result: "11% reduction in student absences · 100% green chemistry adoption · unified billing across 14 sites.",
    image: "https://images.pexels.com/photos/8617715/pexels-photo-8617715.jpeg",
  },
  {
    slug: "class-a-tower",
    sector: "Commercial",
    title: "Class A Office Tower — 28 Floors",
    challenge: "Tenant satisfaction scores were declining and elevator/lobby finishes showed visible wear.",
    solution: "Day porter program, refinished terrazzo, weekly QA walks with property management, and tenant-portal requests.",
    result: "Tenant satisfaction 71 → 92 · LEED O+M points retained · 18-month retention bonus achieved.",
    image: "https://images.pexels.com/photos/36286291/pexels-photo-36286291.jpeg",
  },
  {
    slug: "distribution-hub",
    sector: "Distribution",
    title: "Regional Distribution Hub — 850,000 sq ft",
    challenge: "24/7 operations demanded floor cleanliness without disrupting forklift traffic or shift changes.",
    solution: "Zoned scrubber routes, night-shift deep-clean rotation, and monthly ops-review with logistics leadership.",
    result: "Zero slip-and-fall incidents in 24 months · 18% lower cleaning spend · uptime maintained at 99.9%.",
    image: "https://images.pexels.com/photos/1267324/pexels-photo-1267324.jpeg",
  },
];

export const JOBS = [
  {
    title: "Commercial Janitorial / Cleaning Technician",
    location: "Triad Region, NC",
    type: "Part-time",
    shift: "Evenings & Nights",
    description:
      "Perform daily janitorial cleaning of commercial, medical, and government facilities — including trash removal, restroom sanitation, dusting, vacuuming, and disinfecting high-touch surfaces. Follow site-specific SOPs, use color-coded microfiber, and document QA inspections.",
  },
  {
    title: "General Maintenance Technician",
    location: "Triad Region, NC",
    type: "Part-time",
    shift: "Days",
    description:
      "Handle light repairs and preventative maintenance across client facilities — light plumbing, minor electrical, drywall patching, painting touch-ups, door/hardware repairs, ceiling tile replacement, and general handyman work. Respond to service tickets and log completed work.",
  },
  {
    title: "Floor Care / Floor Maintenance Specialist",
    location: "Triad Region, NC",
    type: "Part-time",
    shift: "Nights & Weekends",
    description:
      "Operate autoscrubbers, burnishers, and extractors. Perform strip-and-wax, buff, refinish, and deep-clean programs on VCT, terrazzo, concrete, hardwood, and luxury vinyl. Maintain equipment, follow chemical safety protocols, and deliver hospitality-grade finishes.",
  },
];

export const TESTIMONIALS = [
  {
    quote: "Empire Serv is the first vendor to walk into our infection-control meeting with a documented plan — and then execute it. Our HAI numbers improved within a quarter.",
    name: "Dana R.",
    role: "Director of Environmental Services, Regional Health System",
  },
  {
    quote: "Their QASP paperwork is airtight. Our contracting officer noted it as one of the cleanest audit trails on the vehicle. Renewal was straightforward.",
    name: "Michael P.",
    role: "Facilities Program Manager, Federal Agency",
  },
  {
    quote: "We consolidated 14 schools onto one program with Empire. Absences dropped, our custodial spend flattened, and my phone stopped ringing.",
    name: "Kevin L.",
    role: "Operations Director, Public School District",
  },
  {
    quote: "We're a 24/7 plant. Empire integrates with our shift changes, our safety huddles, and our Kaizen program like they're part of the team.",
    name: "Amelia S.",
    role: "Plant Manager, Advanced Manufacturing",
  },
];

export const WHY_EMPIRE = [
  { us: "Self-performing, W-2 workforce with paid training", them: "Subcontracted, transient labor" },
  { us: "ISO 9001 QASP inspections with client dashboard", them: "Occasional walkthroughs, paper checklists" },
  { us: "Contract-ready: SAM.gov, cleared personnel available", them: "Not registered on federal contract vehicles" },
  { us: "24/7 emergency response within 4 hours", them: "Callback in 24–48 hours, if at all" },
  { us: "Green Seal & EPA Safer Choice chemistries by default", them: "Legacy chemistries, harsher on people and facilities" },
  { us: "Named account manager + escalation matrix", them: "Rotating dispatcher; no single point of accountability" },
];

export const TRUSTED_BY = [
  "Regional Health System",
  "Federal Agency",
  "State of NC",
  "Advanced Manufacturing",
  "Class A REIT",
  "Public School District",
  "3PL Distribution",
  "Municipal City Hall",
];
